<template>
  <v-container fluid class="pa-0">
    <h2 class="subheading grey--text">Solicitudes</h2>
    <p class="grey--text text--darken-1">{{ counts.Pendiente }} pendientes de revisión · {{ counts['En revisión'] }} en revisión</p>

    <v-card class="elevation-2 mt-2">
      <v-progress-linear v-if="loading" indeterminate></v-progress-linear>
      <v-card-title>
        <v-text-field
          v-model="search"
          prepend-inner-icon="mdi-magnify"
          label="Buscar solicitud"
          outlined
          dense
          hide-details
          clearable
          style="max-width: 320px;"
        ></v-text-field>
      </v-card-title>
      <v-data-table
        :headers="headers"
        :items="solicitudes"
        :search="search"
        :footer-props="{
          itemsPerPageOptions: [{text: '5', value: 5}, {text: '10', value: 10}, {text: 'Todos', value: -1}],
          itemsPerPageText: 'Filas por página'
        }"
        :no-data-text="'No hay solicitudes registradas'"
        class="elevation-0"
      >
        <template v-slot:item.tipo="{ item }">
          <v-icon small class="mr-1">{{ tipoIcon(item.tipo) }}</v-icon>{{ item.tipo.replace('Solicitud de ', '').replace('Solicitud ', '') }}
        </template>
        <template v-slot:item.descripcion="{ item }">
          <div class="font-weight-medium">{{ item.solicitante }}</div>
          <div class="caption grey--text text-wrap" style="max-width: 280px;">{{ item.descripcion }}</div>
        </template>
        <template v-slot:item.prioridad="{ item }">
          <v-chip small :color="prioridadColor(item.prioridad)" text-color="white">{{ item.prioridad }}</v-chip>
        </template>
        <template v-slot:item.estado="{ item }">
          <v-chip small :color="estadoColor(item.estado)" text-color="white">{{ item.estado }}</v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-icon small @click="openDetail(item)">mdi-eye-outline</v-icon>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="dialog" scrollable max-width="560">
      <v-card v-if="selected" class="elevation-5">
        <v-toolbar dark color="primary">
          <v-toolbar-title class="text-truncate">{{ selected.descripcion }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text class="pt-5">
          <v-chip small outlined class="mr-2">{{ selected.tipo }}</v-chip>
          <v-chip small :color="prioridadColor(selected.prioridad)" text-color="white" class="mr-2">{{ selected.prioridad }}</v-chip>
          <v-chip small :color="estadoColor(selected.estado)" text-color="white">{{ selected.estado }}</v-chip>
          <v-row dense class="mt-4">
            <v-col cols="6"><div class="overline grey--text">Solicitante</div><div>{{ selected.solicitante }}</div></v-col>
            <v-col cols="6"><div class="overline grey--text">Fecha</div><div>{{ selected.fecha }}</div></v-col>
          </v-row>
          <div class="overline grey--text mt-3 mb-2">Historial</div>
          <v-timeline dense>
            <v-timeline-item small v-for="(h, i) in selected.historial" :key="i" color="grey">
              <div class="caption grey--text">{{ h.fecha }}</div>
              <div>{{ h.texto }}</div>
            </v-timeline-item>
          </v-timeline>
        </v-card-text>
        <v-card-actions>
          <v-btn outlined small @click="solicitarInfo()" :loading="loadingAction" :disabled="loadingAction">Solicitar información</v-btn>
          <v-spacer></v-spacer>
          <v-btn text color="red" @click="rechazar()" :loading="loadingAction" :disabled="loadingAction">Rechazar</v-btn>
          <v-btn color="primary" @click="aprobar()" :loading="loadingAction" :disabled="loadingAction">Aprobar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import solicitudesService, { extractApiErrorMessage } from '@/services/solicitudesService'
import { estadoColor, prioridadColor } from './mesaDirectivaMockData'

export default {
  name: 'MesaDirectivaSolicitudes',
  data () {
    return {
      solicitudes: [],
      search: '',
      dialog: false,
      selected: null,
      loading: false,
      loadingAction: false,
      headers: [
        { text: 'Tipo', value: 'tipo' },
        { text: 'Solicitud', value: 'descripcion' },
        { text: 'Fecha', value: 'fecha' },
        { text: 'Prioridad', value: 'prioridad' },
        { text: 'Estado', value: 'estado' },
        { text: '', value: 'actions', sortable: false, align: 'end' }
      ]
    }
  },
  computed: {
    counts () {
      const c = { Pendiente: 0, 'En revisión': 0 }
      this.solicitudes.forEach(s => { if (c[s.estado] !== undefined) c[s.estado]++ })
      return c
    }
  },
  async mounted () {
    await this.loadSolicitudes()
  },
  methods: {
    estadoColor,
    prioridadColor,
    tipoIcon (tipo) {
      const map = {
        'Solicitud de evento': 'mdi-calendar-star',
        'Solicitud de modificación': 'mdi-pencil-outline',
        'Solicitud de mantenimiento especial': 'mdi-wrench-outline',
        'Solicitud administrativa': 'mdi-file-document-outline',
        'Solicitud de residente': 'mdi-account-outline'
      }
      return map[tipo] || 'mdi-inbox-outline'
    },
    async loadSolicitudes () {
      this.loading = true
      try {
        this.solicitudes = await solicitudesService.getSolicitudes()
      } catch (error) {
        const message = extractApiErrorMessage(error)
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_change_message', `Error al cargar solicitudes: ${message}`)
        this.$store.commit('snackbar_activate_true')
      } finally {
        this.loading = false
      }
    },
    openDetail (item) {
      this.selected = item
      this.dialog = true
    },
    async aprobar () {
      this.loadingAction = true
      try {
        const solicitudActualizada = await solicitudesService.cambiarEstado(
          this.selected.id,
          'Aprobada',
          'Aprobada por la Mesa Directiva'
        )
        this.selected = solicitudActualizada
        this.$store.commit('snackbar_change_color', 'success')
        this.$store.commit('snackbar_change_message', 'Solicitud aprobada')
        this.$store.commit('snackbar_activate_true')
        this.dialog = false
        await this.loadSolicitudes()
      } catch (error) {
        const message = extractApiErrorMessage(error)
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_change_message', `Error al aprobar: ${message}`)
        this.$store.commit('snackbar_activate_true')
      } finally {
        this.loadingAction = false
      }
    },
    async rechazar () {
      this.loadingAction = true
      try {
        const solicitudActualizada = await solicitudesService.cambiarEstado(
          this.selected.id,
          'Rechazada',
          'Rechazada por la Mesa Directiva'
        )
        this.selected = solicitudActualizada
        this.$store.commit('snackbar_change_color', 'warning')
        this.$store.commit('snackbar_change_message', 'Solicitud rechazada')
        this.$store.commit('snackbar_activate_true')
        this.dialog = false
        await this.loadSolicitudes()
      } catch (error) {
        const message = extractApiErrorMessage(error)
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_change_message', `Error al rechazar: ${message}`)
        this.$store.commit('snackbar_activate_true')
      } finally {
        this.loadingAction = false
      }
    },
    async solicitarInfo () {
      this.loadingAction = true
      try {
        const solicitudActualizada = await solicitudesService.cambiarEstado(
          this.selected.id,
          'En revisión',
          'Se solicitó información adicional al solicitante'
        )
        this.selected = solicitudActualizada
        this.$store.commit('snackbar_change_color', 'info')
        this.$store.commit('snackbar_change_message', 'Se solicitó información adicional')
        this.$store.commit('snackbar_activate_true')
        await this.loadSolicitudes()
      } catch (error) {
        const message = extractApiErrorMessage(error)
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_change_message', `Error al solicitar información: ${message}`)
        this.$store.commit('snackbar_activate_true')
      } finally {
        this.loadingAction = false
      }
    }
  }
}
</script>
